import {
  TCPropertyImportAssignmentMode,
  TCPropertyImportPropertyScope,
  TCPropertyImportSyncMissingPolicy,
} from 'src/api/generated';
import type { TCPropertyImportOptions } from 'src/api/generated';
import { isRecord } from 'src/modules/shared/utils/types.util';

type IntegrationConfigRecord = Record<string, unknown> | null | undefined;

const parseAssignment = (
  value: unknown
): TCPropertyImportOptions['assignment'] | null => {
  if (!isRecord(value)) {
    return null;
  }

  const mode = value.mode;
  if (mode === TCPropertyImportAssignmentMode.AUTO) {
    return { mode: TCPropertyImportAssignmentMode.AUTO };
  }
  if (mode === TCPropertyImportAssignmentMode.KEEP) {
    return { mode: TCPropertyImportAssignmentMode.KEEP };
  }
  if (mode === TCPropertyImportAssignmentMode.FORCE_USER) {
    const userId = value.userId;
    if (typeof userId !== 'number') {
      return null;
    }
    return { mode: TCPropertyImportAssignmentMode.FORCE_USER, userId };
  }
  return null;
};

const parseSyncMissingPolicy = (
  value: unknown
): TCPropertyImportSyncMissingPolicy | undefined => {
  if (value === TCPropertyImportSyncMissingPolicy.REPLACE) {
    return TCPropertyImportSyncMissingPolicy.REPLACE;
  }
  if (value === TCPropertyImportSyncMissingPolicy.UPSERT) {
    return TCPropertyImportSyncMissingPolicy.UPSERT;
  }
  return undefined;
};

const parsePropertyScope = (
  value: unknown
): TCPropertyImportPropertyScope | undefined => {
  if (value === TCPropertyImportPropertyScope.ACTIVE) {
    return TCPropertyImportPropertyScope.ACTIVE;
  }
  if (value === TCPropertyImportPropertyScope.ALL) {
    return TCPropertyImportPropertyScope.ALL;
  }
  return undefined;
};

/**
 * Reads a string property from a free-form integration config object.
 */
export const getConfigStringValue = (
  config: IntegrationConfigRecord,
  key: string
): string => {
  const value = config?.[key];
  return typeof value === 'string' ? value : '';
};

/**
 * Extracts typed property-import options from integration config.
 */
export const getPropertyImportOptionsFromConfig = (
  config: IntegrationConfigRecord
): TCPropertyImportOptions | null => {
  const importOptions = isRecord(config?.importOptions)
    ? config.importOptions
    : null;
  if (!importOptions) {
    return null;
  }

  const assignment = parseAssignment(importOptions.assignment);
  const syncMissingPolicy = parseSyncMissingPolicy(
    importOptions.syncMissingPolicy
  );
  const propertyScope = parsePropertyScope(importOptions.propertyScope);

  if (!assignment && !syncMissingPolicy && !propertyScope) {
    return null;
  }

  return {
    assignment,
    syncMissingPolicy,
    propertyScope,
  };
};

/**
 * Determines whether import options enable auto assignment.
 */
export const isAutoAssignmentEnabledFromConfig = (
  config: IntegrationConfigRecord
): boolean => {
  const importOptions = getPropertyImportOptionsFromConfig(config);
  return (
    importOptions?.assignment?.mode === TCPropertyImportAssignmentMode.AUTO
  );
};

/**
 * Default missing-record policy for integration imports.
 */
export const DEFAULT_IMPORT_SYNC_MISSING_POLICY =
  TCPropertyImportSyncMissingPolicy.UPSERT;

/**
 * Default property scope for integration imports.
 */
export const DEFAULT_IMPORT_PROPERTY_SCOPE = TCPropertyImportPropertyScope.ALL;

/**
 * Resolves missing-record policy from integration config import options.
 */
export const resolveImportSyncMissingPolicy = (
  config: IntegrationConfigRecord
): TCPropertyImportSyncMissingPolicy => {
  const value = getPropertyImportOptionsFromConfig(config)?.syncMissingPolicy;
  return value ?? DEFAULT_IMPORT_SYNC_MISSING_POLICY;
};

/**
 * Resolves property scope from integration config import options.
 */
export const resolveImportPropertyScope = (
  config: IntegrationConfigRecord
): TCPropertyImportPropertyScope => {
  const value = getPropertyImportOptionsFromConfig(config)?.propertyScope;
  return value ?? DEFAULT_IMPORT_PROPERTY_SCOPE;
};

/**
 * Builds typed property-import options payload for integration updates.
 */
export const buildPropertyImportOptions = (params: {
  autoAssignmentEnabled: boolean;
  syncMissingPolicy: TCPropertyImportSyncMissingPolicy;
  propertyScope: TCPropertyImportPropertyScope;
}): TCPropertyImportOptions => {
  return {
    assignment: params.autoAssignmentEnabled
      ? { mode: TCPropertyImportAssignmentMode.AUTO }
      : { mode: TCPropertyImportAssignmentMode.KEEP },
    syncMissingPolicy: params.syncMissingPolicy,
    propertyScope: params.propertyScope,
  };
};
